<template>
  <div>
    <div class="time-point-title">
      {{ $t('stability.timePoints.sectionTitle') }}
    </div>

    <el-row
      v-if="canEdit"
      type="flex"
      justify="end"
      class="time-point-actions"
    >
      <el-col :span="8">
        <el-row
          type="flex"
          justify="end"
        >
          <el-button
            type="primary"
            icon="el-icon-plus"
            size="mini"
            @click="openCreate"
          >
            {{ $t('stability.timePoints.add') }}
          </el-button>
        </el-row>
      </el-col>
    </el-row>

    <div class="table-wrapper">
      <el-table
        :data="timePoints"
        :empty-text="$t('stability.timePoints.empty')"
        border
      >
        <el-table-column
          prop="token"
          :label="$t('stability.token')"
        />

        <el-table-column :label="$t('stability.timePoints.description')">
          <template slot-scope="{ row }">
            {{ row.description || '-' }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('stability.timePoints.fromDate')">
          <template slot-scope="{ row }">
            {{ formatDate(row.fromDate) }}
          </template>
        </el-table-column>

        <!-- Read-only: toDate belongs to the matches and is computed by the
             API from the MAX lead time of their analyses -->
        <el-table-column :label="$t('stability.timePoints.toDate')">
          <template slot-scope="{ row }">
            {{ dueDateLabel(row) }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('stability.timePoints.matchCount')">
          <template slot-scope="{ row }">
            <el-tag
              size="mini"
              :type="matchCount(row) > 0 ? 'info' : 'warning'"
            >
              {{ matchCount(row) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          v-if="canEdit || showsFileAction"
          :label="$t('global.actions')"
        >
          <template slot-scope="{ row }">
            <TableActionButton
              v-if="canEdit"
              :label="$t('tableButtons.edit')"
              button-icon="fas fa-pencil"
              :disabled="deletingId !== null"
              :click-function="() => openEdit(row)"
            />
            <TableActionButton
              v-if="canEdit"
              :label="$t('tableButtons.delete')"
              button-icon="fas fa-trash-alt"
              :loading="deletingId === row.id"
              :disabled="deletingId !== null && deletingId !== row.id"
              :click-function="() => confirmDelete(row)"
            />
            <!-- Files can be attached only once the configuration is
                 configured; a read-only user sees them only to download -->
            <TableActionButton
              v-if="canOpenFile(row)"
              :label="fileActionLabel(row)"
              :button-icon="fileActionIcon(row)"
              :click-function="() => openFile(row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>

    <TimePointDialog
      :visible.sync="dialogVisible"
      :config-id="configId"
      :time-point="editing"
      :existing-tokens="existingTokens"
      @saved="$emit('refresh')"
    />

    <TimePointFileDialog
      :visible.sync="fileDialogVisible"
      :config-id="configId"
      :time-point="fileTimePoint"
      :can-manage="canManage"
      @changed="$emit('refresh')"
    />
  </div>
</template>

<script>
  import moment from 'moment';
  import { stabilityApi } from '@/api';
  import TableActionButton from '@/components/TableActionButton.vue';
  import TimePointDialog from './TimePointDialog.vue';
  import TimePointFileDialog from './TimePointFileDialog.vue';

  export default {
    name: 'TimePointSection',
    components: { TableActionButton, TimePointDialog, TimePointFileDialog },

    props: {
      configId: { type: Number, required: true },
      timePoints: { type: Array, default: () => [] },
      // The API refuses any time point write once the configuration leaves `pending`
      readonly: { type: Boolean, default: false },
      // The file upload is allowed only in `configured`, which `readonly`
      // cannot express: it is also true for `processing` and `error`
      configured: { type: Boolean, default: false },
      canManage: { type: Boolean, default: false },
    },

    data() {
      return {
        dialogVisible: false,
        fileDialogVisible: false,
        editing: null,
        fileTimePointId: null,
        deletingId: null,
      };
    },

    computed: {
      canEdit() {
        return !this.readonly && this.canManage;
      },
      existingTokens() {
        return this.timePoints.map((tp) => tp.token);
      },
      // Derived, not stored: an upload or a removal triggers a reload that
      // replaces every row object, and a stored snapshot would keep showing the
      // state from before it — as if it had failed.
      fileTimePoint() {
        return (
          this.timePoints.find((tp) => tp.id === this.fileTimePointId) || null
        );
      },
      // Keeps the column out of the way when no row would show the action
      showsFileAction() {
        return this.timePoints.some((tp) => this.canOpenFile(tp));
      },
    },

    methods: {
      formatDate(value) {
        if (!value) return '-';
        // Local, not UTC: `fromdate` is a `timestamp without time zone`, so the
        // driver reads the stored midnight back as local time and the API hands
        // us the instant two hours earlier. Formatting in local time undoes it —
        // the same convention as MatchCard.
        return moment(value).format('DD/MM/YYYY');
      },

      matchCount(timePoint) {
        return (timePoint.stabilityMatches || []).length;
      },

      // Each match carries its own toDate, so a time point spans the range of
      // its matches. ISO strings sort chronologically, so plain sort() is enough.
      dueDateLabel(timePoint) {
        const dues = (timePoint.stabilityMatches || [])
          .map((match) => match.toDate)
          .filter(Boolean)
          .sort();
        if (!dues.length) return '-';
        const first = this.formatDate(dues[0]);
        const last = this.formatDate(dues[dues.length - 1]);
        return first === last ? first : `${first} – ${last}`;
      },

      openCreate() {
        this.editing = null;
        this.dialogVisible = true;
      },

      openEdit(timePoint) {
        this.editing = timePoint;
        this.dialogVisible = true;
      },

      hasFiles(timePoint) {
        return (timePoint.files || []).length > 0;
      },

      // Without the manage permission there is nothing to do on a time point
      // that carries no file, so the action stays hidden
      canOpenFile(timePoint) {
        return this.configured && (this.canManage || this.hasFiles(timePoint));
      },

      // Same distinction the app already makes on the analyses status table
      // (config/columns/analysesStatus.js): file-upload while there is nothing
      // yet, paperclip once at least one attachment exists.
      fileActionIcon(timePoint) {
        return this.hasFiles(timePoint)
          ? 'fas fa-paperclip'
          : 'fas fa-file-upload';
      },

      fileActionLabel(timePoint) {
        return this.hasFiles(timePoint)
          ? this.$t('stability.timePoints.fileTitle')
          : this.$t('stability.timePoints.fileUpload');
      },

      openFile(timePoint) {
        this.fileTimePointId = timePoint.id;
        this.fileDialogVisible = true;
      },

      async confirmDelete(timePoint) {
        const count = this.matchCount(timePoint);
        // Deleting a time point cascades onto its matches and their analyses; the
        // conditions survive and stay available to the other time points
        const message =
          count > 0
            ? this.$t('stability.timePoints.deleteWithMatchesConfirm', {
                token: timePoint.token,
                count,
              })
            : this.$t('stability.timePoints.deleteConfirm', {
                token: timePoint.token,
              });

        try {
          await this.$confirm(message, this.$t('global.warning'), {
            confirmButtonText: this.$t('stability.timePoints.delete'),
            cancelButtonText: this.$t('global.cancel'),
          });
        } catch {
          return;
        }

        this.deletingId = timePoint.id;
        try {
          await stabilityApi.deleteTimePoint(this.configId, timePoint.id);
          this.$emit('refresh');
        } catch (err) {
          this.$notify({
            type: 'error',
            showClose: true,
            message:
              (err.response &&
                err.response.data &&
                err.response.data.error &&
                err.response.data.error.message) ||
              this.$t('stability.errors.generic'),
          });
        } finally {
          this.deletingId = null;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  // Same weight and size as the configuration title (.el-descriptions__title,
  // overridden to 1.1rem in StabilityConfiguration.vue): this is a section
  // heading, not a table caption, so it does not follow the muted
  // .table-title look.
  .time-point-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: $--color-text-primary;
    margin-bottom: 0.75rem;
  }

  .time-point-actions {
    margin-bottom: 0.75rem;
  }
</style>
